import { Phone, MapPin, Clock, Heart, Youtube, Instagram } from 'lucide-react';
import { businessInfo } from '../data/menuData';
import { useTheme } from '../context/ThemeContext';

// WhatsApp icon component
const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// MKD Bakery Logo - High Quality White Version (same for both modes)
const LightModeLogo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/uuvmafm4_image.png"
    alt="MKD Bakery Logo"
    className="h-14 w-14 rounded-full object-cover"
    style={{ imageRendering: 'crisp-edges' }}
    loading="eager"
  />
);

const DarkModeLogo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/uuvmafm4_image.png"
    alt="MKD Bakery Logo"
    className="h-14 w-14 rounded-full object-cover"
    style={{ imageRendering: 'crisp-edges' }}
    loading="eager"
  />
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const whatsappLink = `https://wa.me/${businessInfo.whatsapp}?text=Hello%20MKD%20Bakery!%20I%20would%20like%20to%20place%20an%20order.`;
  const { isDarkMode } = useTheme();

  const quickLinks = [
    { name: 'Home', href: '#hero' },
    { name: 'About Us', href: '#about' },
    { name: 'Menu', href: '#menu' },
    { name: 'Special Offers', href: '#specials' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-bakery-text dark:bg-gray-900 text-bakery-bg" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="mb-6">
              {isDarkMode ? <DarkModeLogo /> : <LightModeLogo />}
            </div>
            <p className="text-bakery-bg/80 leading-relaxed">
              {businessInfo.tagline}. Your trusted local bakery in Tegeta, 
              Dar es Salaam, serving fresh baked goods daily.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              <a
                href={businessInfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-full hover:opacity-90 transition-opacity"
                data-testid="footer-instagram"
              >
                <Instagram size={20} className="text-white" />
              </a>
              <a
                href={businessInfo.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-red-600 rounded-full hover:bg-red-700 transition-colors"
                data-testid="footer-youtube"
              >
                <Youtube size={20} className="text-white" />
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-[#25D366] rounded-full hover:bg-[#20BD5A] transition-colors"
                data-testid="footer-whatsapp"
              >
                <WhatsAppIcon size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading text-xl font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-bakery-bg/80 hover:text-bakery-primary transition-colors duration-300"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading text-xl font-bold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone size={20} className="text-bakery-primary mt-1 flex-shrink-0" />
                <a href={`tel:${businessInfo.phone}`} className="text-bakery-bg/80 hover:text-bakery-primary transition-colors">
                  {businessInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-bakery-primary mt-1 flex-shrink-0" />
                <span className="text-bakery-bg/80">{businessInfo.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={20} className="text-bakery-primary mt-1 flex-shrink-0" />
                <span className="text-bakery-bg/80">{businessInfo.hours}</span>
              </li>
            </ul>
          </div>

          {/* Order CTA */}
          <div>
            <h3 className="font-heading text-xl font-bold mb-6">Order Now</h3>
            <p className="text-bakery-bg/80 mb-6">
              Ready to taste our delicious baked goods? Order via phone, WhatsApp, or Msosi App!
            </p>
            <div className="space-y-3">
              <a
                href={`tel:${businessInfo.phone}`}
                className="flex items-center justify-center gap-2 bg-bakery-primary text-white px-6 py-3 rounded-full font-medium hover:bg-bakery-primary/90 transition-colors duration-300 w-full"
                data-testid="footer-call-btn"
              >
                <Phone size={18} />
                Call to Order
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full font-medium hover:bg-[#20BD5A] transition-colors duration-300 w-full"
                data-testid="footer-whatsapp-btn"
              >
                <WhatsAppIcon size={18} />
                WhatsApp Order
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-bakery-bg/20 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-bakery-bg/60 text-sm">
              © {currentYear} MKD Bakery. All rights reserved.
            </p>
            <p className="text-bakery-bg/60 text-sm flex items-center gap-1">
              Made with <Heart size={14} className="text-bakery-primary" /> in Dar es Salaam
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
